import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Form, Button } from 'react-bootstrap';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import { callCenterApi } from '@/_services/api/callCenter';
import { BsArrowRepeat as RefreshIcon } from 'react-icons/bs';
import { css } from '@emotion/react';
import dayjs from 'dayjs';

const noteContainer = css`
    padding: 0.5rem 1rem;
    border-radius: 6px;
    border: 1px solid #ccc;
    p {
        margin-bottom: 0.5rem !important;
    }
`;
const noteTitle = css`
    display: flex;
    align-items: center;
    p {
        color: #000000;
        font-weight: bold;
    }
    span {
        display: flex;
        flex-wrap: no-wrap;
        font-size: 12px;
        color: #686868;
        margin-left: 2%;
        vertical-align: center;
        align-self: center;
        margin-bottom: 3px;
    }
`;
const CallCenterNote = ({ callId, onNotesChange }) => {
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    const notesContainerRef = useRef(null);
    const bottomRef = useRef(null);

    const scrollToBottom = () => {
        if (bottomRef.current) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const loadNotes = useCallback(async () => {
        if (!callId) return;
        try {
            setIsLoading(true);
            const fetched = await callCenterApi.getCallNotes(callId);
            const notesData = Array.isArray(fetched) ? fetched : [];
            setNotes(notesData);
            if (onNotesChange) {
                onNotesChange(notesData);
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
            toast.error(error?.message || 'Failed to load call notes');
        } finally {
            setIsLoading(false);
            setHasLoaded(true);
        }
    }, [callId, onNotesChange]);

    // Scroll to bottom after notes are loaded
    useEffect(() => {
        if (!isLoading && notes.length > 0) {
            scrollToBottom();
        }
    }, [isLoading, notes.length]);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    const handleSave = async () => {
        if (!callId || !newNote.trim()) return;
        try {
            setIsSaving(true);
            const created = await callCenterApi.createCallNote(callId, { text: newNote.trim() });
            const updatedNotes = [...(notes || []), created];
            setNotes(updatedNotes);
            setNewNote('');
            if (onNotesChange) {
                onNotesChange(updatedNotes);
            }
            setTimeout(scrollToBottom, 100);
        } catch (error) {
            toast.error(error?.message || 'Failed to save note');
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSave();
        }
    };

    if (isLoading && !hasLoaded) {
        return (
            <div className="text-center p-4">
                <VscLoading className="spin" fontSize="32" />
            </div>
        );
    }

    return (
        <div className="h-100 d-flex flex-column">
            <div>
                <div className="d-flex justify-content-between mb-3">
                    <div>
                        <strong>Call Notes</strong>
                    </div>
                    <div>
                        <Button variant="outline-primary" onClick={loadNotes} style={{ padding: '0px 4px' }}>
                            <RefreshIcon className={`${isLoading ? 'spin' : ''} mb-1`} />
                        </Button>
                    </div>
                </div>
                <hr />
            </div>

            <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                {notes && notes.length > 0 ? (
                    <div ref={notesContainerRef} style={{ overflowY: 'auto', maxHeight: '60vh' }} className="h-100">
                        {notes.map((note) => (
                            <div key={note._id} className="mb-4" css={noteContainer}>
                                <div css={noteTitle}>
                                    <p>{(note?.createdBy || {})?.name || ''} </p>
                                    <span>{dayjs(note?.createdAt || null).format('MM-DD-YYYY h:mm A')} </span>
                                </div>
                                <p>{note.text}</p>
                            </div>
                        ))}
                        <div ref={bottomRef} />
                    </div>
                ) : (
                    <div
                        className="d-flex text-center flex-column justify-content-center align-items-center p-4"
                        style={{ overflowY: 'auto', maxHeight: '50vh' }}
                    >
                        <h2>No Notes Yet!</h2>
                    </div>
                )}

                <div className="mt-auto">
                    <Form.Group>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={newNote}
                            onChange={(event) => setNewNote(event.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder=""
                            className="border-primary border-4"
                            style={{ borderWidth: '1px' }}
                        />
                    </Form.Group>

                    <div className="d-flex justify-content-end mt-3">
                        <Button variant="primary" onClick={handleSave} disabled={isSaving || !newNote.trim()}>
                            {isSaving && <VscLoading className="spin" />} Save Note
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CallCenterNote;
